import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../utils/theme';
import { Users, TrendingUp, TrendingDown, Zap, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import DemoModeBanner from '../DemoModeBanner';

/**
 * LineupOptimizer - Lineup analysis and optimization UI
 *
 * Features:
 * - 5-man lineup PPP rankings
 * - Synergy scores between players
 * - "Whole > Parts" analysis
 * - Substitution recommendations
 */

// Sample lineup data (would come from API)
const SAMPLE_LINEUPS = [
  {
    id: 1,
    players: [
      { name: 'Sergio Llull', number: 23, position: 'PG' },
      { name: 'Mario Hezonja', number: 8, position: 'SG' },
      { name: 'Dzanan Musa', number: 5, position: 'SF' },
      { name: 'Guerschon Yabusele', number: 0, position: 'PF' },
      { name: 'Walter Tavares', number: 22, position: 'C' }
    ],
    offPPP: 1.12,
    defPPP: 0.98,
    netPPP: 0.14,
    minutes: 245,
    synergy: 88,
    recommendation: 'PLAY MORE - Elite efficiency and chemistry'
  },
  {
    id: 2,
    players: [
      { name: 'Facundo Campazzo', number: 7, position: 'PG' },
      { name: 'Fabien Causeur', number: 1, position: 'SG' },
      { name: 'Mario Hezonja', number: 8, position: 'SF' },
      { name: 'Serge Ibaka', number: 9, position: 'PF' },
      { name: 'Walter Tavares', number: 22, position: 'C' }
    ],
    offPPP: 1.08,
    defPPP: 0.94,
    netPPP: 0.14,
    minutes: 112,
    synergy: 85,
    recommendation: 'PLAY MORE - Strong defensive unit'
  },
  {
    id: 3,
    players: [
      { name: 'Sergio Llull', number: 23, position: 'PG' },
      { name: 'Fabien Causeur', number: 1, position: 'SG' },
      { name: 'Dzanan Musa', number: 5, position: 'SF' },
      { name: 'Guerschon Yabusele', number: 0, position: 'PF' },
      { name: 'Vincent Poirier', number: 17, position: 'C' }
    ],
    offPPP: 1.15,
    defPPP: 1.04,
    netPPP: 0.11,
    minutes: 89,
    synergy: 72,
    recommendation: 'SITUATIONAL - Good offensive unit'
  },
  {
    id: 4,
    players: [
      { name: 'Facundo Campazzo', number: 7, position: 'PG' },
      { name: 'Mario Hezonja', number: 8, position: 'SG' },
      { name: 'Dzanan Musa', number: 5, position: 'SF' },
      { name: 'Serge Ibaka', number: 9, position: 'PF' },
      { name: 'Vincent Poirier', number: 17, position: 'C' }
    ],
    offPPP: 0.94,
    defPPP: 0.88,
    netPPP: 0.06,
    minutes: 67,
    synergy: 78,
    recommendation: 'SITUATIONAL - Defensive specialist lineup'
  },
  {
    id: 5,
    players: [
      { name: 'Carlos Alocen', number: 13, position: 'PG' },
      { name: 'Fabien Causeur', number: 1, position: 'SG' },
      { name: 'Dzanan Musa', number: 5, position: 'SF' },
      { name: 'Guerschon Yabusele', number: 0, position: 'PF' },
      { name: 'Walter Tavares', number: 22, position: 'C' }
    ],
    offPPP: 0.88,
    defPPP: 1.02,
    netPPP: -0.14,
    minutes: 54,
    synergy: 45,
    recommendation: 'AVOID - Negative efficiency'
  }
];

// Sample synergy pairs
const SYNERGY_PAIRS = [
  { player1: 'Llull', player2: 'Tavares', synergy: 92, reason: 'Elite PnR chemistry' },
  { player1: 'Campazzo', player2: 'Tavares', synergy: 88, reason: 'Lob threat spacing' },
  { player1: 'Hezonja', player2: 'Yabusele', synergy: 85, reason: 'Wing-forward DHO' },
  { player1: 'Causeur', player2: 'Ibaka', synergy: 82, reason: 'Catch-and-shoot spacing' },
  { player1: 'Llull', player2: 'Musa', synergy: 78, reason: 'Secondary ballhandler combo' }
];

const ANTI_SYNERGY_PAIRS = [
  { player1: 'Alocen', player2: 'Campazzo', synergy: 34, reason: 'Redundant skillsets' },
  { player1: 'Poirier', player2: 'Tavares', synergy: 28, reason: 'Poor spacing - both rim-bound' }
];

function LineupCard({ lineup, rank, isDarkMode, expanded, onToggle }) {
  const netPPPColor = lineup.netPPP >= 0.10 ? 'text-green-500' :
    lineup.netPPP >= 0 ? 'text-blue-500' : 'text-red-500';

  const recommendationBg = lineup.recommendation.includes('PLAY MORE')
    ? 'bg-green-500/10 border-green-500/30'
    : lineup.recommendation.includes('AVOID')
      ? 'bg-red-500/10 border-red-500/30'
      : 'bg-yellow-500/10 border-yellow-500/30';

  return (
    <div className={`rounded-xl border transition-all ${
      'bg-gray-900 border-gray-800'
    }`}>
      {/* Header Row */}
      <div
        className="p-4 cursor-pointer flex items-center justify-between"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
            rank <= 2 ? 'bg-green-500/20 text-green-500' :
              rank >= 4 ? 'bg-red-500/20 text-red-500' : 'bg-gray-500/20 text-gray-400'
          }`}>
            #{rank}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {lineup.players.map((p, i) => (
                <span
                  key={i}
                  className={`text-sm ${'text-gray-300'}`}
                >
                  {p.name.split(' ').pop()}{i < lineup.players.length - 1 && ','}
                </span>
              ))}
            </div>
            <span className={`text-xs ${'text-gray-500'}`}>
              {lineup.minutes} min
            </span>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className={`text-lg font-bold ${netPPPColor}`}>
              {lineup.netPPP >= 0 ? '+' : ''}{lineup.netPPP.toFixed(2)}
            </div>
            <div className={`text-xs ${'text-gray-500'}`}>
              Net PPP
            </div>
          </div>
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className={`px-4 pb-4 border-t ${'border-gray-800'}`}>
          <div className="pt-4 space-y-4">
            {/* Player list */}
            <div className="grid grid-cols-5 gap-2">
              {lineup.players.map((p, i) => (
                <div
                  key={i}
                  className={`text-center p-2 rounded-lg ${'bg-gray-800'}`}
                >
                  <div className={`text-xs ${'text-gray-500'}`}>
                    {p.position}
                  </div>
                  <div className={`text-sm font-medium ${'text-white'}`}>
                    #{p.number}
                  </div>
                  <div className={`text-xs ${'text-gray-400'}`}>
                    {p.name.split(' ').pop()}
                  </div>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className={`text-lg font-bold ${'text-green-400'}`}>
                  {lineup.offPPP.toFixed(2)}
                </div>
                <div className={`text-xs ${'text-gray-500'}`}>
                  Off PPP
                </div>
              </div>
              <div className="text-center">
                <div className={`text-lg font-bold ${'text-blue-400'}`}>
                  {lineup.defPPP.toFixed(2)}
                </div>
                <div className={`text-xs ${'text-gray-500'}`}>
                  Def PPP
                </div>
              </div>
              <div className="text-center">
                <div className={`text-lg font-bold ${netPPPColor}`}>
                  {lineup.netPPP >= 0 ? '+' : ''}{lineup.netPPP.toFixed(2)}
                </div>
                <div className={`text-xs ${'text-gray-500'}`}>
                  Net PPP
                </div>
              </div>
              <div className="text-center">
                <div className={`text-lg font-bold ${
                  lineup.synergy >= 80 ? 'text-green-500' :
                    lineup.synergy >= 60 ? 'text-yellow-500' : 'text-red-500'
                }`}>
                  {lineup.synergy}
                </div>
                <div className={`text-xs ${'text-gray-500'}`}>
                  Synergy
                </div>
              </div>
            </div>

            {/* Recommendation */}
            <div className={`p-3 rounded-lg border ${recommendationBg}`}>
              <p className={`text-sm font-medium ${'text-white'}`}>
                {lineup.recommendation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LineupOptimizer({ teamId }) {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  const [lineups, setLineups] = useState(SAMPLE_LINEUPS);
  const [expandedLineup, setExpandedLineup] = useState(1);
  const [sortBy, setSortBy] = useState('netPPP');
  const [showSynergies, setShowSynergies] = useState(false);

  // Sort lineups
  const sortedLineups = useMemo(() => {
    const sorted = [...lineups];
    switch (sortBy) {
      case 'offPPP':
        sorted.sort((a, b) => b.offPPP - a.offPPP);
        break;
      case 'defPPP':
        sorted.sort((a, b) => a.defPPP - b.defPPP);
        break;
      case 'minutes':
        sorted.sort((a, b) => b.minutes - a.minutes);
        break;
      case 'synergy':
        sorted.sort((a, b) => b.synergy - a.synergy);
        break;
      default:
        sorted.sort((a, b) => b.netPPP - a.netPPP);
    }
    return sorted;
  }, [lineups, sortBy]);

  return (
    <div className="space-y-4">
      <DemoModeBanner compact message="Lineup data uses Real Madrid EuroLeague sample. Connect your lineup feed for real data." />
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className={`text-lg font-semibold ${'text-white'}`}>
            Lineup Optimizer
          </h3>
          <p className={`text-sm ${'text-gray-400'}`}>
            5-man lineup efficiency rankings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={`px-3 py-1.5 rounded-lg text-sm ${
              'bg-gray-800 text-white border-gray-700'
            } border`}
          >
            <option value="netPPP">Sort by Net PPP</option>
            <option value="offPPP">Sort by Offensive PPP</option>
            <option value="defPPP">Sort by Defensive PPP</option>
            <option value="minutes">Sort by Minutes</option>
            <option value="synergy">Sort by Synergy</option>
          </select>
          <button
            onClick={() => setShowSynergies(!showSynergies)}
            className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 ${
              showSynergies
                ? 'bg-orange-500 text-white'
                : 'bg-gray-800 text-gray-300 border border-gray-700'
            }`}
          >
            <Zap size={14} />
            Synergies
          </button>
        </div>
      </div>

      {/* Synergy Panel */}
      {showSynergies && (
        <div className={`p-4 rounded-xl ${'bg-gray-900 border border-gray-800'}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Positive Synergies */}
            <div>
              <h4 className={`text-sm font-medium mb-3 flex items-center gap-2 ${'text-green-400'}`}>
                <TrendingUp size={16} />
                Best Synergies (Play Together)
              </h4>
              <div className="space-y-2">
                {SYNERGY_PAIRS.map((pair, i) => (
                  <div
                    key={i}
                    className={`p-2 rounded-lg flex items-center justify-between ${
                      'bg-green-500/10'
                    }`}
                  >
                    <div>
                      <span className={`font-medium ${'text-white'}`}>
                        {pair.player1} + {pair.player2}
                      </span>
                      <p className={`text-xs ${'text-gray-400'}`}>
                        {pair.reason}
                      </p>
                    </div>
                    <span className="text-green-500 font-bold">{pair.synergy}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Anti-Synergies */}
            <div>
              <h4 className={`text-sm font-medium mb-3 flex items-center gap-2 ${'text-red-400'}`}>
                <AlertTriangle size={16} />
                Anti-Synergies (Avoid Together)
              </h4>
              <div className="space-y-2">
                {ANTI_SYNERGY_PAIRS.map((pair, i) => (
                  <div
                    key={i}
                    className={`p-2 rounded-lg flex items-center justify-between ${
                      'bg-red-500/10'
                    }`}
                  >
                    <div>
                      <span className={`font-medium ${'text-white'}`}>
                        {pair.player1} + {pair.player2}
                      </span>
                      <p className={`text-xs ${'text-gray-400'}`}>
                        {pair.reason}
                      </p>
                    </div>
                    <span className="text-red-500 font-bold">{pair.synergy}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lineup List */}
      <div className="space-y-3">
        {sortedLineups.map((lineup, index) => (
          <LineupCard
            key={lineup.id}
            lineup={lineup}
            rank={index + 1}
            isDarkMode={isDarkMode}
            expanded={expandedLineup === lineup.id}
            onToggle={() => setExpandedLineup(expandedLineup === lineup.id ? null : lineup.id)}
          />
        ))}
      </div>

      {/* Summary */}
      <div className={`p-4 rounded-xl ${'bg-gray-900/50 border border-gray-800'}`}>
        <h4 className={`text-sm font-medium mb-2 ${'text-gray-300'}`}>
          Key Insights
        </h4>
        <ul className={`text-sm space-y-1 ${'text-gray-400'}`}>
          <li>Best lineup: Llull-Hezonja-Musa-Yabusele-Tavares (+0.14 Net PPP)</li>
          <li>Avoid: Alocen with starting unit (poor synergy)</li>
          <li>Llull + Tavares PnR is elite (92 synergy score)</li>
        </ul>
      </div>
    </div>
  );
}
